// src/pages/Login.jsx
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../components/Toast/useToast";

export default function Login() {
  const { showToast } = useToast();
  const { signIn, isAuthenticated, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const from = location.state?.from?.pathname || null;

  useEffect(() => {
    // Redirigir si está autenticado, aunque no tenga role todavía (claims pueden tardar)
    if (isAuthenticated && !authLoading) {
      const redirectPath = from && from !== '/login' ? from
        : role === 'super_admin' ? '/super-admin'
        : role === 'company_admin' ? '/admin'
        : '/upload';
      navigate(redirectPath, { replace: true });
    }
  }, [isAuthenticated, role, authLoading, navigate, from]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      showToast({ type: "error", message: "Introduce email y contraseña" });
      return;
    }
    setLoading(true);
    console.log('🔐 Iniciando login con:', email);
    try {
      await signIn(email, password);
      console.log('✅ Login exitoso');
      showToast({ type: "success", message: "¡Bienvenido!" });
    } catch (error) {
      console.error('❌ Error en login:', error);
      let message = "Error al iniciar sesión";
      if (error.code === "auth/invalid-credential" || error.code === "auth/wrong-password") {
        message = "Email o contraseña incorrectos";
      } else if (error.code === "auth/user-not-found") {
        message = "Usuario no encontrado";
      } else if (error.code === "auth/too-many-requests") {
        message = "Demasiados intentos. Intenta más tarde";
      } else if (error.code === "auth/network-request-failed") {
        message = "Error de conexión. Verifica tu internet";
      }
      showToast({ type: "error", message });
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0b1028', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#9fb0e0', fontSize: 14 }}>Verificando sesión...</p>
      </div>
    );
  }

  return (
    <div style={s.root}>
      {/* ── Panel izquierdo oscuro ── */}
      <aside style={s.left}>
        {/* Logo top-left */}
        <div style={s.logoRow}>
          <img src="/dataforge_blue_64.png" alt="DataForge" style={s.logoImg} />
          <div>
            <div style={s.brandName}>DATAFORGE</div>
            <div style={s.brandSub}>SIGNOS</div>
          </div>
        </div>

        {/* Tagline central */}
        <div style={s.taglineWrap}>
          <h2 style={s.tagline}>
            Firma digital,<br />
            <span style={s.taglineAccent}>sin complicaciones.</span>
          </h2>
          <p style={s.taglineDesc}>
            Gestiona y firma documentos de forma segura desde cualquier dispositivo.
          </p>

          {/* Features */}
          <div style={s.features}>
            {[
              { icon: '🔒', title: 'Seguro', desc: 'Datos cifrados y autenticación robusta.' },
              { icon: '⚡', title: 'Tiempo real', desc: 'Sincronización instantánea entre dispositivos.' },
              { icon: '🏢', title: 'Multiempresa', desc: 'Gestiona varias organizaciones desde una cuenta.' },
            ].map(f => (
              <div key={f.title} style={s.featureItem}>
                <div style={s.featureIcon}>{f.icon}</div>
                <div>
                  <div style={s.featureTitle}>{f.title}</div>
                  <div style={s.featureDesc}>{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <p style={s.leftFooter}>© 2026 DATAFORGE · Todos los derechos reservados</p>
      </aside>

      {/* ── Panel derecho blanco ── */}
      <main style={s.right}>
        <div style={s.formWrap}>
          {/* Logo pequeño arriba */}
          <div style={s.logoRow}>
            <img src="/dataforge_blue_64.png" alt="DataForge" style={s.logoImg} />
            <div>
              <div style={{ ...s.brandName, color: '#273067' }}>DATAFORGE</div>
              <div style={{ ...s.brandSub, color: '#9fb0e0' }}>SIGNOS</div>
            </div>
          </div>

          <h1 style={s.formTitle}>Inicia sesión</h1>
          <p style={s.formSubtitle}>Accede a tu cuenta para continuar</p>

          <form onSubmit={handleLogin} style={s.form}>
            <div>
              <label style={s.label}>Correo electrónico</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@empresa.com"
                autoComplete="email"
                disabled={loading}
                style={s.input}
                onFocus={(e) => e.target.style.borderColor = '#4f6cf7'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              />
            </div>

            <div>
              <label style={s.label}>Contraseña</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                disabled={loading}
                style={s.input}
                onFocus={(e) => e.target.style.borderColor = '#4f6cf7'}
                onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{ ...s.btn, opacity: loading ? 0.7 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
              onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = '#1e2654'; }}
              onMouseLeave={(e) => { if (!loading) e.currentTarget.style.background = '#273067'; }}
            >
              {loading ? '⏳ Iniciando sesión...' : '→ Entrar'}
            </button>
          </form>

          <p style={s.rightFooter}>DataForge SignOS · Sistema profesional de firma</p>
        </div>
      </main>

      <style>{`
        @media (max-width: 700px) {
          .login-left { display: none !important; }
          .login-right { width: 100% !important; }
        }
      `}</style>
    </div>
  );
}

const s = {
  root: {
    display: 'flex',
    minHeight: '100vh',
  },
  // ── Izquierda ──
  left: {
    width: '50%',
    background: '#0b1028',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '32px 40px',
    boxSizing: 'border-box',
  },
  logoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  logoImg: {
    width: 36,
    height: 36,
    objectFit: 'contain',
    borderRadius: 8,
  },
  brandName: {
    fontWeight: 800,
    fontSize: 14,
    color: '#e9eefc',
    letterSpacing: 1,
    lineHeight: 1.1,
  },
  brandSub: {
    fontSize: 9,
    color: '#4f6cf7',
    letterSpacing: 3,
    marginTop: 2,
  },
  taglineWrap: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: 24,
    paddingBottom: 40,
  },
  tagline: {
    margin: 0,
    fontSize: 36,
    fontWeight: 700,
    color: '#e9eefc',
    lineHeight: 1.25,
  },
  taglineAccent: {
    color: '#4f6cf7',
  },
  taglineDesc: {
    margin: 0,
    color: '#6b80b8',
    fontSize: 14,
    lineHeight: 1.6,
    maxWidth: 320,
  },
  features: {
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
    marginTop: 8,
  },
  featureItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 14,
  },
  featureIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    background: 'rgba(79,108,247,0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 15,
    flexShrink: 0,
  },
  featureTitle: {
    color: '#c5d0f5',
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 2,
  },
  featureDesc: {
    color: '#6b80b8',
    fontSize: 12,
  },
  leftFooter: {
    color: '#2d3a6b',
    fontSize: 11,
    margin: 0,
  },
  // ── Derecha ──
  right: {
    width: '50%',
    background: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 32px',
    boxSizing: 'border-box',
  },
  formWrap: {
    width: '100%',
    maxWidth: 380,
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
  },
  formTitle: {
    margin: '28px 0 6px',
    fontSize: 28,
    fontWeight: 700,
    color: '#111827',
  },
  formSubtitle: {
    margin: '0 0 28px',
    color: '#6b7280',
    fontSize: 14,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
  },
  label: {
    display: 'block',
    color: '#374151',
    fontSize: 13,
    fontWeight: 500,
    marginBottom: 6,
  },
  input: {
    width: '100%',
    background: '#f9fafb',
    color: '#111827',
    border: '1px solid #d1d5db',
    borderRadius: 10,
    padding: '11px 14px',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  },
  btn: {
    width: '100%',
    background: '#273067',
    color: '#ffffff',
    border: 'none',
    borderRadius: 10,
    padding: '13px',
    fontSize: 15,
    fontWeight: 600,
    transition: 'background 0.2s',
    marginTop: 4,
    letterSpacing: 0.3,
  },
  rightFooter: {
    color: '#9ca3af',
    fontSize: 11,
    textAlign: 'center',
    marginTop: 32,
    marginBottom: 0,
  },
};
